/**
 * Toaster component - Displays toast notifications
 */
import * as React from "react"
import { useToast } from "../../hooks/use-toast"
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from "lucide-react"
import { cn } from "../../lib/utils"

const toastIcons = {
    default: null,
    success: CheckCircle,
    error: AlertCircle,
    warning: AlertTriangle,
    info: Info,
}

const toastStyles = {
    default: "bg-white border-gray-200",
    success: "bg-green-50 border-green-200 text-green-800",
    error: "bg-red-50 border-red-200 text-red-800",
    warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
    info: "bg-blue-50 border-blue-200 text-blue-800",
}

const iconStyles = {
    default: "text-gray-500",
    success: "text-green-500",
    error: "text-red-500",
    warning: "text-yellow-500",
    info: "text-blue-500",
}

export function Toaster() {
    const { toasts, dismiss } = useToast()

    return (
        <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none">
            {toasts.map((toast) => {
                const Icon = toastIcons[toast.type || "default"]
                const styleClass = toastStyles[toast.type || "default"]
                const iconClass = iconStyles[toast.type || "default"]

                return (
                    <div
                        key={toast.id}
                        className={cn(
                            "pointer-events-auto rounded-lg border p-4 shadow-lg",
                            "transform transition-all duration-300 ease-out",
                            toast.open
                                ? "translate-x-0 opacity-100"
                                : "translate-x-full opacity-0",
                            styleClass
                        )}
                        role="alert"
                    >
                        <div className="flex items-start gap-3">
                            {Icon && (
                                <Icon className={cn("h-5 w-5 flex-shrink-0 mt-0.5", iconClass)} />
                            )}
                            <div className="flex-1 min-w-0">
                                {toast.title && (
                                    <p className="font-semibold text-sm">{toast.title}</p>
                                )}
                                {toast.description && (
                                    <p className="text-sm opacity-90 mt-1">{toast.description}</p>
                                )}
                            </div>
                            <button
                                onClick={() => dismiss(toast.id)}
                                className="flex-shrink-0 rounded-md p-1 hover:bg-black/5 transition-colors"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        {toast.action && (
                            <div className="mt-3">{toast.action}</div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}
